import { useSearch } from '@tanstack/react-router';
import { useMemo } from 'react';

import type { Location } from '../../../../entities/post/models';
import { DataTable, type TableColumnConfig } from '../../../../shared/components/data-table';
import { useOverviewLocations } from '../../shared/hooks';

import { useSharedDateRange } from '@/shared/hooks/use-shared-date-range';
import { usePublicFilter } from '../../shared/public-filter/use-public-filter';

interface Props {
  onLocationClick?: (location: Location, event: React.MouseEvent) => void;
  onNavigate?: () => void;
  title?: string;
  pageSize: number;
}

export function SimpleLocationTable({
  onLocationClick,
  onNavigate,
  title = 'Top locations',
  pageSize,
}: Props) {
  const { q } = useSearch({ strict: false }) as { q: string };
  const { dateRange } = useSharedDateRange();
  const { publicFilter } = usePublicFilter(q);
  const { data: locations, isLoading } = useOverviewLocations({
    queryId: q,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    limit: 5,
    publicFilter,
  });

  const columns: TableColumnConfig<Location>[] = useMemo(() => {
    return [
      {
        key: 'name',
        header: 'Locations',
        size: '1fr',
        cell: ({ getValue }) => {
          return <DataTable.Cell getValue={getValue} />;
        },
      },
      {
        key: 'postCount',
        header: 'Volume',
        size: '100px',
        cell: ({ getValue }) => {
          return <DataTable.Cell getValue={getValue} />;
        },
      },
    ];
  }, []);

  return (
    <DataTable
      data={locations ?? []}
      isLoading={isLoading}
      header={<DataTable.Header title={title} onRightClick={onNavigate} />}
      columns={columns}
      onRowClick={(location, event) => onLocationClick?.(location, event)}
      pageSize={pageSize}
      enablePagination={false}
      showIndexColumn={false}
    />
  );
}
